/**
 * 목록 쪽 나누기 — 받아 온 줄을 쪽으로 자른다(2026-10-21, 콘텐츠 목록부터).
 *
 * 서버의 쪽(커서)으로 나누지 않는 이유: 콘솔의 목록은 받은 것 **전부** 안에서 정렬한다(제목·크기·편성 수는
 * 서버가 정렬하지 않는다). 서버 쪽으로 자르면 "제목 가나다순" 의 2쪽이 전체의 2쪽이 아니라 **두 번째로 받은
 * 묶음**의 가나다순이 된다. 그래서 받은 것을 정렬한 뒤 그리는 것만 쪽으로 나눈다 — 수천 장의 썸네일을 한
 * 번에 그리지 않고, 끝없는 스크롤에서 보던 자리를 잃지 않는다.
 */

/** 한 쪽에 보이는 수 — 그리드가 2·3·4열이라 어느 너비에서도 마지막 줄이 꽉 차게 12의 배수로. */
export const PAGE_SIZES = [24, 48, 96] as const;
export const DEFAULT_PAGE_SIZE: number = PAGE_SIZES[0];

export interface PageSlice<T> {
  rows: T[];
  /** 실제로 보이는 쪽 — 요청이 끝을 넘으면 마지막 쪽이다. */
  page: number;
  pageCount: number;
  /** 보이는 첫 줄·끝 줄의 차례(1부터). 비었으면 둘 다 0. */
  from: number;
  to: number;
  total: number;
}

/**
 * `page` 쪽을 자른다.
 *
 * 끝을 넘는 쪽은 마지막 쪽으로 본다 — 마지막 쪽의 콘텐츠를 모두 보관·삭제했거나, 걸러서 줄었는데 주소에
 * 옛 쪽 번호가 남아 있을 때 빈 쪽을 보이지 않는다. 주소는 고치지 않는다(받는 동안 0건이라 1쪽으로
 * 고쳐 버리면, 다 받은 뒤 보던 쪽으로 돌아오지 못한다).
 */
export function paginate<T>(rows: readonly T[], page: number, size: number): PageSlice<T> {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.floor(page)), pageCount);
  const start = (current - 1) * size;
  const slice = rows.slice(start, start + size);
  return {
    rows: slice,
    page: current,
    pageCount,
    from: slice.length > 0 ? start + 1 : 0,
    to: start + slice.length,
    total,
  };
}

/** 주소의 `?page` 읽기 — 숫자가 아니거나 1보다 작으면 1쪽. */
export function parsePageParam(value: string): number {
  const page = Number(value);
  return Number.isInteger(page) && page >= 1 ? page : 1;
}

/** 저장해 둔 쪽당 수 읽기 — 고를 수 있는 값이 아니면 기본값. */
export function parsePageSize(value: string | null): number {
  const size = Number(value);
  return (PAGE_SIZES as readonly number[]).includes(size) ? size : DEFAULT_PAGE_SIZE;
}

/**
 * 쪽당 수를 바꿀 때 갈 쪽 — 보던 첫 줄이 들어 있는 쪽.
 *
 * 1쪽으로 돌려보내면 48개씩 보며 3쪽까지 내려온 사람이 96개로 넓히는 순간 처음부터 다시 찾는다.
 */
export function pageForRow(row: number, size: number): number {
  return row >= 1 ? Math.floor((row - 1) / size) + 1 : 1;
}

/**
 * 쪽 번호 단추 — 처음·끝과 지금 쪽 앞뒤 하나만, 나머지는 `'gap'`(…).
 *
 * 칸 수가 늘 7개 이하라 쪽이 몇 개든 한 줄에 선다. 줄임표는 둘 이상 건너뛸 때만 선다 — 끝 가까이에서
 * 그쪽 다섯을 채우므로 "1 … 3" 처럼 한 칸을 아끼지도 못하면서 번호를 숨기는 줄임표는 생기지 않는다.
 */
export function pageNumbers(page: number, pageCount: number): Array<number | 'gap'> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
  const shown = new Set<number>([1, pageCount, page - 1, page, page + 1]);
  // 끝 가까이에서는 그쪽 다섯을 채운다 — 칸 수가 쪽마다 들쭉날쭉하면 단추가 눌린 자리에서 움직인다.
  if (page <= 4) for (let n = 2; n <= 5; n += 1) shown.add(n);
  if (page >= pageCount - 3) for (let n = pageCount - 4; n < pageCount; n += 1) shown.add(n);
  const sorted = [...shown].filter((n) => n >= 1 && n <= pageCount).sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  for (const n of sorted) {
    const last = out[out.length - 1];
    if (typeof last === 'number' && n - last > 1) out.push('gap');
    out.push(n);
  }
  return out;
}
